import { useEffect, useCallback, useState } from "react";
import { useParams } from "react-router";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { useDebounce } from "use-debounce";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  selectSelectedBrandState,
  selectBrandState,
  selectActiveBrandsState,
} from "../../../store/selectors/brands";
import {
  selectOverviewDomainsRows,
  selectOverviewSourceUsageShowSkeleton,
  selectSourceDetailLinksState,
  selectSourceDetailUrlsChart,
  selectSelectedModelIdState,
} from "../../../store/selectors/overview";
import { selectLastUpdateTimeByViewBrandIdState } from "../../../store/selectors/scores";
import { setViewBrandId } from "../../../store/brands/slice";
import { fetchSourceDetailLinks } from "../../../store/overview/slice";
import { useOverviewDateFilterFromUrl } from "../../Overview/hooks/useOverviewDateFilterFromUrl";
import { useOverviewDataFetch } from "../../Overview/hooks/useOverviewDataFetch";
import { DateSelectionMode, OverviewPeriod } from "../../../components/DatePeriodSelector/types";
import { exportLinksToExcel } from "../utils/exportLinksToExcel";
import type { SourceDetailPageData } from "./types";

const SEARCH_DEBOUNCE_MS = 300;
const LINKS_PAGE_SIZE = 20;

export function useSourceDetailPage(): SourceDetailPageData {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const { dateFilter, setDateFilterFromUrl } = useOverviewDateFilterFromUrl();
  useOverviewDataFetch();

  const brands = useAppSelector(selectActiveBrandsState);
  const selectedBrand = useAppSelector(selectSelectedBrandState);
  const mainBrand = useAppSelector(selectBrandState);
  const lastUpdateTime = useAppSelector(selectLastUpdateTimeByViewBrandIdState);
  const domainRows = useAppSelector(selectOverviewDomainsRows);
  const showSkeleton = useAppSelector(selectOverviewSourceUsageShowSkeleton);
  const sourceDetailLinks = useAppSelector(selectSourceDetailLinksState);
  const selectedModelId = useAppSelector(selectSelectedModelIdState);
  const chart = useAppSelector(selectSourceDetailUrlsChart);

  const domainId = id != null ? parseInt(id, 10) : NaN;
  const domainRow =
    id != null ? (domainRows.find((r) => r.id === id) ?? null) : null;

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const [linksPage, setLinksPage] = useState(1);

  useEffect(() => {
    if (!selectedBrand && mainBrand && brands?.length) {
      dispatch(setViewBrandId(mainBrand.id));
    }
  }, [selectedBrand, mainBrand, brands, dispatch]);

  useEffect(() => {
    if (!Number.isNaN(domainId) && selectedBrand != null) {
      const modelIdPayload =
        selectedModelId != null ? { model_id: selectedModelId } : {};
      if (dateFilter.type === DateSelectionMode.Date) {
        appDispatch(
          fetchSourceDetailLinks({
            domain_id: domainId,
            date: format(dateFilter.dateTs, "yyyy-MM-dd"),
            ...modelIdPayload,
          })
        );
      } else {
        const period: 7 | 30 =
          dateFilter.period === OverviewPeriod.ThirtyDays ? 30 : 7;
        appDispatch(
          fetchSourceDetailLinks({
            domain_id: domainId,
            period,
            ...modelIdPayload,
          })
        );
      }
    }
  }, [
    domainId,
    dateFilter,
    selectedBrand,
    selectedModelId,
    appDispatch,
  ]);

  const links = sourceDetailLinks.data ?? [];
  const searchLower = debouncedSearch.trim().toLowerCase();
  const filteredLinks =
    searchLower === ""
      ? links
      : links.filter(
          (row) =>
            row.url.toLowerCase().includes(searchLower) ||
            (row.title && row.title.toLowerCase().includes(searchLower)) ||
            String(row.user).includes(searchLower) ||
            String(row.citations).includes(searchLower) ||
            (row.is_mentioned ? "yes" : "no").includes(searchLower)
        );

  useEffect(() => {
    setLinksPage(1);
  }, [dateFilter, debouncedSearch]);

  const linksTotal = filteredLinks.length;
  const linksTotalPages = Math.max(1, Math.ceil(linksTotal / LINKS_PAGE_SIZE));
  const safeLinksPage = Math.min(Math.max(1, linksPage), linksTotalPages);
  const linksPageRows = filteredLinks.slice(
    (safeLinksPage - 1) * LINKS_PAGE_SIZE,
    safeLinksPage * LINKS_PAGE_SIZE
  );

  const handleLinksPageChange = useCallback((page: number) => {
    setLinksPage(page);
  }, []);

  const handleExportToExcel = useCallback(() => {
    exportLinksToExcel(filteredLinks);
  }, [filteredLinks]);

  const showChartSkeleton =
    showSkeleton ||
    sourceDetailLinks.loading === "loading" ||
    sourceDetailLinks.loading === "idle";
  const chartEmpty = !chart.data?.length || !chart.series?.length;

  return {
    domainId,
    domainRow,
    overviewReady: !showSkeleton,
    dateFilter,
    setDateFilterFromUrl,
    lastUpdateTime,
    chart,
    showChartSkeleton,
    chartEmpty,
    sourceDetailLinks: {
      loading: sourceDetailLinks.loading,
      error: sourceDetailLinks.error,
      data: sourceDetailLinks.data,
    },
    links,
    filteredLinks,
    linksPageRows,
    linksTotal,
    linksPageSize: LINKS_PAGE_SIZE,
    linksCurrentPage: safeLinksPage,
    onLinksPageChange: handleLinksPageChange,
    searchInput,
    setSearchInput,
    handleExportToExcel,
  };
}
